import { SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { LineDot } from "recharts/types/cartesian/Line";
import { Updater, useImmer } from "use-immer";

import { TimeSeriesColors } from "@intelligems/ig-graphs/colors";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { TimeSeriesData } from "@intelligems/ig-types/schemas/analytics/graphs";
import { hexToRgbA } from "@intelligems/ig-utils/common";
import { Stack, useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useOrgTimezone } from "@src/api/organizations/get-organization";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { ReferenceLineTooltip } from "@src/features/analytics/metricDetails/timeSeries/TimeSeriesComponentHelpers";
import { getReferenceLinesFromExperienceActions } from "@src/features/analytics/utils";
import { TimeSeriesDrawerContent } from "@src/features/mobileAnalytics/drawers/TimeSeriesDrawerContent";
import { useMobileDrawer } from "@src/features/mobileAnalytics/shared/hooks/useMobileDrawer";
import { DrawerTypes } from "@src/features/mobileAnalytics/shared/providers/types";
import { MobileGraphIntervalButtons } from "@src/features/mobileAnalytics/testDetails/components/MobileGraphIntervalButtons";
import { MobileLegendItem } from "@src/features/mobileAnalytics/testDetails/components/MobileLegendItem";
import { MobileTimeSeriesSkeleton } from "@src/features/mobileAnalytics/testDetails/components/MobileTimeSeriesSkeleton";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";

interface MobileTimeSeriesGraphProps {
  graphConfigs: GraphConfig[];
  selectedMetricId: string;
  graphInterval: string;
  setGraphInterval: Updater<string>;
  data: TimeSeriesData[];
  cumulative: boolean;
  setCumulative: Updater<boolean>;
  confidenceInterval: boolean;
  setConfidenceInterval: Updater<boolean>;
  isLoading: boolean;
}

const customDotRenderer = ({
  data,
  id,
  activeIndex,
}: {
  id: string;
  data: TimeSeriesData[];
  activeIndex: number;
}): LineDot => {
  type CustomDotProps = Pick<
    React.SVGProps<SVGCircleElement>,
    "cx" | "cy" | "stroke"
  > & {
    index: number;
  };

  return ({ cx, cy, index, stroke }: CustomDotProps) => {
    if (index === activeIndex && data[index]?.[id] !== undefined) {
      return <circle key={index} cx={cx} cy={cy} r={5} fill={stroke} />;
    }

    return <></>;
  };
};

export const MobileTimeSeriesGraph = ({
  graphConfigs,
  setGraphInterval,
  graphInterval,
  selectedMetricId,
  data,
  cumulative,
  setCumulative,
  confidenceInterval,
  setConfidenceInterval,
  isLoading,
}: MobileTimeSeriesGraphProps) => {
  const theme = useTheme();
  const timezone = useOrgTimezone(true) as string;
  const drawer = useMobileDrawer();

  const getDataValue = (index: number) => {
    if (!data || data.length === 0) {
      return undefined;
    }
    return data.at(index);
  };

  const [legendDataIndex, setLegendDataIndex] = useImmer<number>(-1);
  const legendValue = getDataValue(legendDataIndex);

  const selectedConfig = useMemo(() => {
    return (
      graphConfigs.find((c) => c.impactMetric === selectedMetricId) ||
      graphConfigs.find((c) => c.metrics[0] === selectedMetricId) ||
      graphConfigs[0]
    );
  }, [selectedMetricId, graphConfigs]);

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const variations = useSelector(selectedVariations);
  const graphValueFormatter = useGraphValueFormatter();
  const experience = useSelector(selectedExperience);

  const hasNoData =
    !isLoading && (!data || data.length < 1 || data.length === 0);

  const fakeLineData = useMemo(() => {
    return [
      "Page A",
      "Page B",
      "Page C",
      "Page D",
      "Page E",
      "Page F",
      "Page G",
    ].map((point) => ({
      name: point,
      ...Object.fromEntries(
        variations.map((variation) => [
          variation.id,
          Math.floor(Math.random() * 3) + 1,
        ])
      ),
    }));
  }, [variations]);

  const hasConfidenceIntervalData = useMemo(() => {
    if (!data[5]?.[`${variations[0].id}-range`]) {
      return false;
    }
    return true;
  }, [data, variations]);

  // Initialize legend to show the last data point
  useEffect(() => {
    if (!data || data.length === 0) {
      setLegendDataIndex(-1);
      return;
    }
    setLegendDataIndex(data.length - 1);
  }, [data, setLegendDataIndex]);

  // Register the chart options drawer if drawer management is provided
  // Register/update drawer whenever relevant state changes
  useEffect(() => {
    drawer.registerDrawer({
      type: DrawerTypes.TIME_SERIES_OPTIONS,
      header: "Time Series Options",
      content: (
        <TimeSeriesDrawerContent
          cumulative={cumulative}
          setCumulative={setCumulative}
          confidenceInterval={confidenceInterval}
          setConfidenceInterval={setConfidenceInterval}
          isLoading={isLoading}
          hasNoData={hasNoData}
          hasConfidenceIntervalData={hasConfidenceIntervalData}
        />
      ),
    });
  }, [
    cumulative,
    confidenceInterval,
    isLoading,
    hasNoData,
    hasConfidenceIntervalData,
  ]);

  return (
    <Stack spacing={2}>
      {/* Time granularity buttons */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <MobileGraphIntervalButtons
          graphInterval={graphInterval}
          hasNoData={hasNoData}
          isLoading={isLoading}
          setGraphInterval={setGraphInterval}
        />
        <SlidersHorizontal
          onClick={() => drawer.openDrawer(DrawerTypes.TIME_SERIES_OPTIONS)}
          style={{ cursor: "pointer" }}
        />
      </Stack>

      {/* Chart section */}
      {hasNoData ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <IgTypography>
            No Data - Your filters may have resulted in no data, try resetting
            them
          </IgTypography>
        </Stack>
      ) : isLoading ? (
        <MobileTimeSeriesSkeleton fakeLineData={fakeLineData} />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={240} debounce={150}>
            <ComposedChart
              data={data}
              margin={{ top: 10, right: 5, left: 0, bottom: 5 }}
              onClick={(e) => {
                // On mobile, toggle tooltip on click
                let targetIndex = data.length - 1;
                if (e?.activeTooltipIndex !== undefined) {
                  targetIndex = e?.activeTooltipIndex;
                }
                setLegendDataIndex(targetIndex);
              }}
            >
              <CartesianGrid
                style={{ stroke: theme.igBorders.core.disabled }}
                vertical={false}
              />
              <XAxis
                domain={["dataMin", "dataMax"]}
                dataKey={"xAxisKey"}
                tickLine={false}
                interval="preserveStartEnd"
                tickCount={3}
                axisLine={{ stroke: theme.igBorders.core.disabled }}
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 10,
                }}
                height={30}
              />
              <YAxis
                type={"number"}
                domain={["auto", "auto"]}
                axisLine={{ stroke: theme.igBorders.core.disabled }}
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 10,
                }}
                tickLine={false}
                tickCount={4}
                width={45}
                tickFormatter={(value: any) =>
                  graphValueFormatter({
                    isAxisTick: true,
                    value,
                    configDataTypes: selectedConfig.dataTypes,
                  })!
                }
              />

              {getReferenceLinesFromExperienceActions(
                experience.experienceActions
              ).map((action) => {
                return (
                  <ReferenceLine
                    key={action.id}
                    x={action.actionTs / 1000}
                    stroke="rgba(0, 0, 0, 0.23)"
                    strokeDasharray={"5 5"}
                    isFront={true}
                    label={
                      <Label
                        content={(props: any) => {
                          return (
                            <ReferenceLineTooltip
                              props={props}
                              experienceAction={action}
                              timezone={timezone}
                              locale={storeCurrencyConfig!.value.locale}
                            />
                          );
                        }}
                      />
                    }
                  />
                );
              })}

              {confidenceInterval && hasConfidenceIntervalData
                ? variations.map((variation, index) => (
                    <Area
                      activeDot={false}
                      key={`${variation.id}-area`}
                      type="monotone"
                      dataKey={`${variation.id}-interval`}
                      stroke="none"
                      fill={hexToRgbA(TimeSeriesColors[index], 0.3)}
                      connectNulls={false}
                      animationDuration={700}
                    />
                  ))
                : null}
              {variations.map((variation, index) => (
                <Line
                  type="monotone"
                  key={`${variation.id}-line`}
                  dataKey={variation.id}
                  stroke={TimeSeriesColors[index]}
                  dot={customDotRenderer({
                    activeIndex: legendDataIndex,
                    data,
                    id: variation.id,
                  })}
                  connectNulls={false}
                  animationDuration={700}
                  strokeWidth={2}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>

          {/* Selected timestamp display */}
          {legendValue && (
            <IgTypography
              variant="caption"
              fontWeight={600}
              sx={{ color: theme.palette.text.secondary }}
            >
              {legendValue.xAxisKey}
            </IgTypography>
          )}

          {/* Mobile legend - vertical layout */}
          <Stack spacing={1} sx={{ pt: 2 }}>
            {variations.map((variation, index) => {
              const value = legendValue?.[variation.id] as number | undefined;
              const ciValue = legendValue?.[`${variation.id}-range`] as
                | number
                | undefined;

              return (
                <MobileLegendItem
                  key={variation.id}
                  label={variation.name}
                  color={TimeSeriesColors[index]}
                  value={value}
                  confidenceInterval={ciValue}
                  dataType={selectedConfig.dataTypes[0]}
                  variant="body1"
                />
              );
            })}
          </Stack>
        </>
      )}
    </Stack>
  );
};
